// Module 18 contracts (ADR-020): API clients and tokens, webhook subscriptions and deliveries,
// connectors and health, devices. Secrets appear only in the one response that creates them.
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const Version = z.number().int().min(1);
const Reason = z.string().trim().min(3, 'v.reasonShort').max(500, 'v.tooLong');

// ------------------------------------------------------------------ API clients (PB-18-01, PB-18-06)
export const ApiScope = z.object({ action: z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/, 'v.invalid'), siteId: Uuid.nullable() });
export const CreateApiClientRequest = z.object({
  name: trimmed(100),
  description: optionalText(500),
  scopes: z.array(ApiScope).min(1, 'v.required').max(60),
  rateLimitPerMinute: z.number().int().min(1, 'v.range').max(100_000, 'v.range').optional(),
  tokenDays: z.number().int().min(1, 'v.range').max(365, 'v.range').optional(),
  reason: Reason,
});
export const UpdateApiClientScopesRequest = z.object({ expectedVersion: Version, scopes: z.array(ApiScope).min(1).max(60), reason: Reason });
export const RotateApiTokenRequest = z.object({ overlapHours: z.number().int().min(0, 'v.range').max(168, 'v.range').default(24), tokenDays: z.number().int().min(1).max(365).optional() });
export const RevokeRequest = z.object({ reason: Reason });

export interface ApiTokenView {
  readonly id: string;
  readonly hint: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
  readonly state: 'active' | 'expired' | 'revoked';
}
export interface ApiClientView {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: 'active' | 'disabled';
  readonly rateLimitPerMinute: number;
  readonly scopes: ReadonlyArray<{ readonly action: string; readonly siteId: string | null; readonly grantId: string }>;
  readonly tokens: readonly ApiTokenView[];
  readonly createdAt: string;
  readonly version: number;
}
export interface ApiTokenIssued {
  readonly client: ApiClientView;
  /** Shown once; only its SHA-256 digest is stored. */
  readonly token: string;
  readonly expiresAt: string;
}

// ------------------------------------------------------------------ webhooks (PB-18-02, PB-18-03)
export const CreateWebhookRequest = z.object({
  name: trimmed(100),
  url: z.string().trim().min(8).max(2000),
  eventTypes: z.array(z.string().regex(/^[a-z][a-z0-9_.]{2,99}$/, 'v.invalid')).min(1, 'v.required').max(50),
  siteId: Uuid.nullable().optional(),
});
export const UpdateWebhookRequest = z.object({
  expectedVersion: Version,
  name: trimmed(100).optional(),
  eventTypes: z.array(z.string().regex(/^[a-z][a-z0-9_.]{2,99}$/, 'v.invalid')).min(1).max(50).optional(),
});
export const WebhookCommandRequest = z.object({ expectedVersion: Version, reason: optionalText(300) });
export const RotateWebhookSecretRequest = z.object({ expectedVersion: Version, overlapHours: z.number().int().min(0).max(168).default(24) });
export const ReplayDeliveryRequest = z.object({ reason: Reason });

export interface WebhookEventTypeView {
  readonly type: string;
  readonly schemaVersion: number;
  readonly description: string;
  readonly readAction: string;
}
export interface WebhookSubscriptionView {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly eventTypes: readonly string[];
  readonly siteId: string | null;
  readonly status: 'pending_verification' | 'active' | 'paused' | 'disabled';
  readonly statusReason: string | null;
  /** Reference only: the last characters of the current secret. Never the secret. */
  readonly secretHint: string;
  readonly secretRotatedAt: string | null;
  readonly previousSecretValidUntil: string | null;
  readonly verifiedAt: string | null;
  readonly lastVerification: { readonly at: string; readonly outcome: string } | null;
  readonly counts: { readonly pending: number; readonly delivered: number; readonly dead: number };
  readonly version: number;
}
export interface WebhookSecretIssued {
  readonly subscription: WebhookSubscriptionView;
  /** Shown once. Stored sealed for signing; never shown again. */
  readonly signingSecret: string;
}
export interface WebhookDeliveryView {
  readonly id: string;
  readonly subscriptionId: string;
  readonly eventId: string;
  readonly eventType: string;
  readonly status: 'pending' | 'delivered' | 'dead' | 'cancelled';
  readonly attempts: number;
  readonly nextAttemptAt: string | null;
  readonly lastStatusCode: number | null;
  readonly lastOutcome: string | null;
  readonly lastError: string | null;
  readonly deliveredAt: string | null;
  readonly deadAt: string | null;
  readonly replayCount: number;
  readonly createdAt: string;
}
export interface WebhookDeliveryDetail extends WebhookDeliveryView {
  /** Exact signed body; headers are shown with the signature replaced by a placeholder. */
  readonly body: string;
  readonly headersPreview: Readonly<Record<string, string>>;
  readonly attemptsLog: ReadonlyArray<{ readonly attemptNo: number; readonly startedAt: string; readonly durationMs: number; readonly statusCode: number | null; readonly outcome: string; readonly error: string | null; readonly responseExcerpt: string | null; readonly replay: boolean }>;
}

// ------------------------------------------------------------------ connectors and health (PB-18-04 .. PB-18-07)
export const CreateIntegrationConnectorRequest = z.object({
  adapterCode: z.string().regex(/^[a-z][a-z0-9_.-]{1,59}$/, 'v.invalid'),
  name: trimmed(100),
  siteId: Uuid.nullable().optional(),
  config: z.record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/), z.union([z.string().max(500), z.number(), z.boolean()])).default({}),
  secretRefs: z.record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/), z.string().max(200)).default({}),
  ownerUserId: Uuid.nullable().optional(),
});
export const ConnectorCommandRequest = z.object({ expectedVersion: Version });
export const ConnectorOperationRequest = z.object({ operation: z.string().regex(/^[a-z][a-z_]{1,39}$/, 'v.invalid'), simulate: z.enum(['confirmed', 'failed', 'unknown']).optional() });
export const ReconcileOperationRequest = z.object({ outcome: z.enum(['confirmed', 'failed']), note: Reason });

export interface AdapterView {
  readonly code: string;
  readonly kind: string;
  readonly label: string;
  readonly operations: readonly string[];
  readonly simulator: boolean;
  readonly validation: string;
  readonly secretKeys: readonly string[];
  readonly blocker: string | null;
}
export interface ConnectorView {
  readonly id: string;
  readonly adapterCode: string;
  readonly adapter: AdapterView | null;
  readonly name: string;
  readonly siteId: string | null;
  readonly config: Readonly<Record<string, unknown>>;
  /** References only (env:NAME / vault:path#key), never values. */
  readonly secretRefs: Readonly<Record<string, string>>;
  readonly status: 'draft' | 'enabled' | 'disabled';
  readonly transport: { readonly state: string; readonly lastHeartbeatAt: string | null; readonly lastError: string | null };
  readonly business: { readonly state: string; readonly lastConfirmedAt: string | null; readonly unknown: number; readonly failed: number };
  readonly lastTest: { readonly at: string; readonly outcome: string } | null;
  readonly version: number;
}
export interface OperationView {
  readonly id: string;
  readonly operation: string;
  readonly outcome: string;
  readonly detail: string | null;
  readonly occurredAt: string;
  readonly reconciledAt: string | null;
  readonly reconciledOutcome: string | null;
}

// ------------------------------------------------------------------ devices (PB-18-12)
export const DEVICE_KIND_VALUES = ['printer', 'scanner', 'reader', 'encoder', 'edge_agent'] as const;
export const CreateDeviceRequest = z.object({
  siteId: Uuid,
  kind: z.enum(DEVICE_KIND_VALUES),
  name: trimmed(80),
  model: optionalText(80),
  staleAfterSeconds: z.number().int().min(30, 'v.range').max(86_400, 'v.range').optional(),
});
export const DevicePairRequest = z.object({ code: z.string().trim().min(6).max(40), firmware: optionalText(40) });
export const DeviceHeartbeatRequest = z.object({
  state: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,30}$/), z.union([z.string().max(60), z.number(), z.boolean()])).optional(),
  job: z.object({ ref: z.string().trim().min(1).max(80), outcome: z.enum(['completed', 'failed', 'unknown']), detail: optionalText(300) }).optional(),
});
export interface DeviceView {
  readonly id: string;
  readonly source: 'device' | 'kiosk';
  readonly siteId: string;
  readonly kind: string;
  readonly name: string;
  readonly model: string | null;
  readonly firmware: string | null;
  readonly status: string;
  readonly connectivity: 'connected' | 'degraded' | 'disconnected' | 'never_connected';
  readonly lastHeartbeatAt: string | null;
  /** Last business result (print/scan job), independent of connectivity. */
  readonly lastJob: { readonly ref: string | null; readonly outcome: string; readonly at: string } | null;
  readonly reportedState: Readonly<Record<string, unknown>>;
  readonly version: number;
}
export interface DevicePairing {
  readonly device: DeviceView;
  /** One-time pairing code, shown once. */
  readonly pairingCode: string;
  readonly expiresAt: string;
}
